import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Col, Divider, Row, Space, Spin } from 'antd'
import { groupBy, isEmpty } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import {
  HistoryLink,
  NodeBreadcrumbs,
} from '../../../common/components/sections'
import { StyledPageSubTitle } from '../../../common/components/styled'
import { EVENT_METADATA_TYPES, METADATA_TYPES } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useCourseTypesQuery,
  useDocumentTitle,
  useIngredientsQuery,
  useUpdatedIngredientSubscription,
} from '../../../common/hooks'
import { flattenTreeStructure } from '../../../common/utils'

import { renderDishTypeEvent } from '../../helpers'
import { useDishTypeQuery } from '../../hooks'
import {
  DishTypeAdditionalIngredientsDetailsForm,
  DishTypeAlternativeIngredientsDetailsForm,
  DishTypeIgnoredDetailsForm,
  DishTypeImageDetailsForm,
  DishTypeNotesForm,
  DishTypeSimpleDetailsForm,
} from '../forms'

const DishTypeDetailsPage = () => {
  const { id } = useParams()
  const filters = useMemo(() => ({ isIgnored: true }), [])

  const isDishTypePageDirty = useSelector(
    store =>
      store.dishTypes.dishType.isAdditionalIngredientsDirty ||
      store.dishTypes.dishType.isAlternativeIngredientsDirty ||
      store.dishTypes.dishType.isIgnoredDirty ||
      store.dishTypes.dishType.isImagesDirty ||
      store.dishTypes.dishType.isSimpleDetailsDirty ||
      store.dishTypes.dishType.isNotesDirty,
  )

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    data,
    refetch,
  } = useDishTypeQuery(id)

  useUpdatedIngredientSubscription(
    id,
    isDishTypePageDirty,
    METADATA_TYPES.DISH_TYPES.value,
    refetch,
  )

  const {
    options: ingredientOptions,
    isLoading: isLoadingIngredients,
    error: ingredientsError,
  } = useIngredientsQuery()

  const {
    options: courseTypeOptions,
    isLoading: isLoadingCourseTypes,
    error: courseTypesError,
  } = useCourseTypesQuery(filters)

  const flatIngredientTreeData = useMemo(
    () => flattenTreeStructure(ingredientOptions),
    [ingredientOptions],
  )

  const groupedFlatIngredientTreeDataByValue = useMemo(
    () => groupBy(flatIngredientTreeData, 'value'),
    [flatIngredientTreeData],
  )

  useDocumentTitle(`${data?.name || 'Loading Dish Type...'}`)

  useEffect(() => {
    if (fetchError || ingredientsError || courseTypesError) {
      showErrorNotification({
        title: 'Dish Type error',
        description: fetchError || ingredientsError || courseTypesError,
      })
    }
  }, [courseTypesError, fetchError, ingredientsError])

  return isFetchLoading || isEmpty(data) ? (
    <Spin />
  ) : (
    <>
      <StyledPageSubTitle>
        <Row justify="space-between">
          <Col span={12}>
            <NodeBreadcrumbs
              metadataType={METADATA_TYPES.DISH_TYPES}
              idPath={data.node.idPath}
              namePath={data.node.namePath}
            />
          </Col>

          <Space>
            <UpdateDataText
              updatedAt={data.updatedAt}
              updatedBy={data.updatedBy?.name}
            />

            <HistoryLink
              renderEvent={renderDishTypeEvent}
              entityId={id}
              eventMetadataTypes={[EVENT_METADATA_TYPES.DISH_TYPE]}
            />
          </Space>
        </Row>
      </StyledPageSubTitle>
      <DishTypeSimpleDetailsForm
        id={data.id}
        name={data.name}
        description={data.description}
        synonyms={data.synonyms}
        position={data.position}
        maxPosition={data.maxPosition}
        isExcludedFromSearch={data.isExcludedFromSearch}
        isExcludedFromBrowsing={data.isExcludedFromBrowsing}
      />
      <Divider />
      {isLoadingIngredients ? (
        <Spin />
      ) : (
        !isEmpty(ingredientOptions) && (
          <>
            <DishTypeAdditionalIngredientsDetailsForm
              ingredientOptions={ingredientOptions}
              dishType={data}
              groupedFlatIngredientTreeDataByValue={
                groupedFlatIngredientTreeDataByValue
              }
              flatIngredientTreeData={flatIngredientTreeData}
            />
            <Divider />
            <DishTypeAlternativeIngredientsDetailsForm
              ingredientOptions={ingredientOptions}
              dishType={data}
              groupedFlatIngredientTreeDataByValue={
                groupedFlatIngredientTreeDataByValue
              }
              flatIngredientTreeData={flatIngredientTreeData}
            />
          </>
        )
      )}
      <Divider />
      {isLoadingCourseTypes ? (
        <Spin />
      ) : (
        !isEmpty(courseTypeOptions) && (
          <DishTypeIgnoredDetailsForm
            dishType={data}
            courseTypeOptions={courseTypeOptions}
          />
        )
      )}
      <Divider />
      <DishTypeImageDetailsForm dishType={data} />
      <Divider />
      <DishTypeNotesForm id={data.id} notes={data.notes} />
    </>
  )
}

export default DishTypeDetailsPage
